import React, { useState, useEffect } from "react";
import { Drawer, Typography, Space, Button, Row, Col, Form, Select } from "antd";
import dayjs from "dayjs";
import minMax from "dayjs/plugin/minMax"; // Import minMax plugin
import metaData from "../../data/metaData";
import { showErrMsg } from "../../utils/error";
import log from "../../utils/log";
import c from "../../utils/common";
import Number from "../General/Number";
import SectionHeader from "../GetStarted/SectionHeader";

const { Title, Text } = Typography;
dayjs.extend(minMax); // Extend dayjs with the minMax plugin

const VoucherDetailDrawer = ({
  open,
  voucher,
  product,
  header,
  closable = true,
  isLoading = false,
  confirmBtnText,
  onClose,
  onConfirm,
}) => {
  const [form] = Form.useForm();
  const [localVoucher, setLocalVoucher] = useState(voucher);
  const [warningMessage, setWarningMessage] = useState(""); // New state for warning message

  const validateForm = async (form) => {
    try {
      await form.validateFields();
    } catch (error) {
      log.error(error);
      throw new Error("Some of the input fields are invalid. Please review and save again");
    }
  };

  const submit = async () => {
    try {
      // Validate the form
      await validateForm(form);
      onConfirm(localVoucher);
    } catch (error) {
      log.debug(error);
      showErrMsg(error);
    }
  };

  // Update local state when voucher prop changes
  useEffect(() => {
    if (voucher) {
      setLocalVoucher(voucher);
    }
  }, [voucher]);

  // Function to update local voucher state
  const updateLocalVoucherData = (updatedData) => {
    setLocalVoucher((prevVoucher) => ({
      ...prevVoucher,
      ...updatedData,
    }));
  };

  return (
    <Drawer
      title={header}
      open={open}
      width="50%"
      onClose={() => {
        setWarningMessage();
        onClose();
      }}
      closable={closable}
      extra={
        <Button
          type="primary"
          size="large"
          loading={isLoading}
          disabled={!localVoucher || !product || warningMessage} // Disable if there's a warning
          onClick={submit}
        >
          {confirmBtnText}
        </Button>
      }
    >
      {!localVoucher || !product ? null : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <img
            style={{
              width: 350,
              objectFit: "cover",
              borderRadius: "0.5rem",
              border: "1px solid var(--ion-color-step-200)",
            }}
            src={metaData.blobPreURL + "clientUpload-" + product.gallery[0] + ".jpeg" + metaData.blobPostURL}
          />
          <SectionHeader title={product.name} description={`price $ ${product.price}`} style={{ margin: 0 }} />

          <Space direction="vertical" style={{ width: "100%" }}>
            <InfoItem label="Available Qty" value={(product?.qty?.available || 0) + (voucher?.qty || 0)} />

            <InfoItem
              label="Qty per collection"
              description="Number of vouchers your customer will receive"
              value={
                <Number
                  state={{
                    data: localVoucher?.qtyPerCollection,
                    form: form,
                    rules: [
                      { required: true, message: "required" },
                      {
                        validator: (_, value) => {
                          const availableQty = (product?.qty?.available || 0) + (voucher?.qty || 0);
                          const maxQtyPerCollection = Math.floor(availableQty, 0);

                          if (value > 0 && value <= maxQtyPerCollection) {
                            return Promise.resolve();
                          } else if (value > maxQtyPerCollection) {
                            return Promise.reject(
                              new Error(
                                `Must be less than or equal to ${maxQtyPerCollection} to avoid exceeding available qty`
                              )
                            );
                          } else {
                            return Promise.reject(new Error("Must be greater than zero"));
                          }
                        },
                      },
                    ],
                    onChange: (value) => value > 0 && updateLocalVoucherData({ qtyPerCollection: value }),
                  }}
                  min={1}
                  keyName="qtyPerCollection"
                  readOnly={false}
                />
              }
            />
          </Space>
        </Space>
      )}
    </Drawer>
  );
};

const InfoItem = ({ label, description, value, icon }) => (
  <Row justify="start" style={{ marginBottom: "1rem" }}>
    <Col span={8}>
      <Space style={{ width: "100%" }}>
        {icon}
        <Text strong>{label}:</Text>
      </Space>
      <Text type="secondary">{description}</Text>
    </Col>
    <Col span={16}>
      <Text strong>{value}</Text>
    </Col>
  </Row>
);

export default VoucherDetailDrawer;
